import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { RemoteError } from "abweb/dto/exception/RemoteError";
import { ChangePasswordResult } from "abweb/dto/generated/user/ChangePasswordResult";
import { LoginHistoryInfo } from "abweb/dto/generated/user/LoginHistoryInfo";
import { UserInfo } from "abweb/dto/user/UserInfo";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LostSessionErrorType } from "abweb/service/authentication/IAuthenticationService";
import { MethodType } from "abweb/service/rpc/MethodType";
import { ProxyRPCService } from "abweb/service/rpc/ProxyRPCService";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { NonAbInitioResponseError } from "abweb/service/utils/rpc/NonAbInitioResponseError";
import { UnexpectedResponseContentError } from "abweb/service/utils/rpc/UnexpectedResponseContentError";
import { Mutex } from "abweb/utils/async/Mutex";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorContextSink } from "abweb/utils/error/ErrorContextSink";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { ErrorUtils } from "abweb/utils/ErrorUtils";
import { TSOGUtils } from "abweb/utils/json/TSOGUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { PromiseUtils } from "abweb/utils/PromiseUtils";
import { URLUtils } from "abweb/utils/URLUtils";
const LOG = LogUtils.getLogger(__filename);

// TESTING NOTE: the AuthenticationServiceUSpec tests are in abweb/client,
// but the AuthenticationServiceXSpec integration tests are in abmtweb/test-webapp/client,
// as that is the first test-webapp that has required authentication configured (requires login to
// access rpcs).

// Elliot believes that the authentication stuff may work in abweb/test-webapp, but is optional.
// So you can 'optionally' login, but it is not required to access anything.
// So may be able to move back the xspec back to abweb/test-webapp, but wouldn't be able to
// verify 'unsecured' rpcs that should return 401 (unauthenticated) in the abweb/test-webapp when
// accessed by a non-logged user.
// So for now, prefer abmtweb/test-webapp, but may revisit this decision later.

export class AuthenticationService {
  constructor(rpcService) {
    _defineProperty(this, "loginMutex", new Mutex());
    _defineProperty(this, "_logoutInProgress", false);
    this.rpcService = rpcService;
  }
  getAuthenticatedUser() {
    return this.rpcService.invokeRetObj(AuthenticationService.USER_INFO_RPC_PATH, null, UserInfo, MethodType.GET).catch(ex => {
      return UserInfo.createAnonymousUser();
    });
  }

  /**
   * Same as above getAuthenticatedUser, but avoids RPCService (and FetchUtils) infrastructure
   * which can 'block' or trigger lost session workflows.
   * 'Probes' should just directly invoke rpc and return raw result.
   * @returns UserInfo instance returned from server, but if any exception thrown it will return an anonymous user
   */
  async getAuthenticatedUserProbe() {
    try {
      const resp = await this.rawJsonFetch(`${AuthenticationService.USER_INFO_RPC_PATH}?authenticatedUserProbe`);
      if (resp.ok /*status == 200s */ || resp.status === 304 /*cached*/) {
        const json = await resp.text();
        const userInfo = TSOGUtils.parse(json, UserInfo);
        if (!userInfo) {
          throw new Error("Parsing UserInfo json returned a null userInfo instance.");
        }
        return userInfo;
      }
      throw new Error(`${AuthenticationService.USER_INFO_RPC_PATH} rpc returned resp with bad status: ` + resp.status);
    } catch (ex) {
      LOG.error("Following exception thrown during getAuthenticatedUserProbe(), so returning anonymous user: " + ErrorUtils.getErrorMsgFromObj(ex));
      return UserInfo.createAnonymousUser();
    }
  }

  /**
   * @inheritDoc
   */
  async login(username, password, reportErrors = true, success_url) {
    try {
      // acceptHeader must be application/json in order to get 403 on bad credentials (otherwise get redirect)
      const reqInit = FetchUtils.buildFormRequest(FetchUtils.MEDIA_TYPE_JSON);
      // Do not auto-follow as not needed, AND can get error if deployment configuration has load balancer (see ABSDK-1828)
      reqInit.redirect = "manual";
      const namedParams = {
        j_username: username,
        j_password: password,
        reportErrors,
        success_url
      };
      await this.loginMutex.runExclusive("Invoking login rpc", () => this.rpcService.invokeVoid("/authenticate/j_spring_security_check", namedParams, reqInit, true, false));
      return this.getAuthenticatedUser();
    } catch (ex) {
      // The server is a funny beast. Sometimes it responds in a way that makes invokeVoid throw. In either case, this
      // REALLY wants to fetch the current authenticated user (if any) and return it. This is typically done from the
      // LoginDlg, where we want to be able to check to see whether the login was successful or not.
      //
      // PR 133132 - In installed applications, it is possible that get response that is not JSON.  We currently rely
      // on re-direction but ultimately we need to make server side handle the client expected response type and reply
      // accordingly.  At this time though that would ripple significantly through the API and authentication schemas
      // such as SSO so this is a tactical change.  The default
      // requires it be a protected resource so is possible that will get a response with a HTTP status of 403 / access
      // denied and if this occurs we want to capture this and continue to call 'getAuthenticatedUser' to get user
      // information.
      const isAccessDenied = ClassUtils.isInstanceOf(ex, RemoteError)
      // Note all AuthenticationException (and subclasses like BadCredentialsException and AuthenticationServiceException)
      // exceptions thrown by appserver will be returned as 403s (see ExceptionResolver::mapExceptionToHttpStatus)
      && ex.response?.status === 403;
      if (reportErrors && !isAccessDenied) {
        throw ex;
      }
      return this.getAuthenticatedUser(); // causes success if non-anonymous user, else 'invalid username or password' msg
    }
  }

  /**
   * @inheritDoc
   */
  async loginViaBrowserRefresh(invokeLogout) {
    try {
      if (invokeLogout) {
        await this.logoutForTesting();
      }
      // use await to ensure ALL prelogoutCleanup steps completed before browser tab reload starts (in caller's 'finally' block)
      await this.preBrowserTabReloadCleanup();
      // This returns a promise that will 'never resolve', so current thread will be awaiting it while browser reloads.
      return new Promise(() => {});
    } catch (err) {
      LOG.error("Error while doing low-level logout(), but browser tab will reload anyway.");
      // This returns a promise that will 'never resolve', so current thread will be awaiting it while browser reloads.
      return new Promise(() => {});
    } finally {
      BrowserUtils.locationReload();
    }
  }

  /**
   * @inheritDoc
   */
  async logout() {
    this._logoutInProgress = true;
    try {
      // pre-emptively logout of direct proxies as part of logout as password is AG token tied to old jsessionid
      ProxyRPCService.reset();
      // use await to ensure ALL prelogoutCleanup steps completed before browser tab reload starts (in caller's 'finally' block)
      await this.preBrowserTabReloadCleanup();
    } catch (err) {
      LOG.error("Error while doing low-level logout(), but browser tab will reload anyway." + `\n  Error: ${ErrorUtils.getErrorMsgFromObj(err)}`);
    } finally {
      // Redirect browser to logout url: DO NOT USE AN AJAX REQUEST!
      BrowserUtils.setLocationHref(URLUtils.getAbsoluteUrl("/authenticate/j_spring_security_logout?useReferer=true"));
    }
    // This returns a promise that will 'never resolve', so current thread will be awaiting it while browser reloads.
    return new Promise(() => {});
  }
  get isLogoutInProgress() {
    return this._logoutInProgress;
  }
  async preBrowserTabReloadCleanup() {
    // suppress rpc errors on shutdown (some arrive after user logged out)
    AbWebServices.getRPCService().setShuttingDown();

    // Turn error off rendering dialog as don't want to bother customers with rpc race condition errors
    // during logout workflow (per discussion w/Gregg, Elliot, Rob, Brad).
    // However, leave logging on so that might be able to put in a breakpoint and see console-logged errors.
    LogAndRenderErrorPipeline.pipeline.renderHandler = new ErrorContextSink();

    // Stop client-side polling of rpcService (but NOT proxied rpcServices).
    // The doTeardown arg is false as no need to invoke rpc to release session as
    // going to logout below (which as side-effect tears down the server-side session)
    await AbWebServices.clientSessionService.releaseSession(false);

    // As about to refresh the browser tab via below logout, and all server and client state will be thrown away,
    // suppress any rpcs that might otherwise be invoked by the teardown service (note 'logout' can also be called
    // when user has no valid session, so suppressing teardown rpcs avoids subsequent authentication errors on rpcs).
    AbWebServices.teardownService().disable();

    // Useful for debugging as dumps out rpc information for the active (running) rpcs.
    // This might be useful for detecting race conditions at logout.
    // ProfileUtils.logInFlightRPCs("In Flight RPC Stats - right before logout");
  }
  async rawJsonFetch(appRelativeUrl) {
    // Can not use: FetchUtils.fetchAndParseJsonFile(appRelativeUrl, {cache: "no-store"})
    // as that fetches and parses response for json payload.  This method returns Response (and not the json payload)
    // return FetchUtils.fetchAndParseJsonFile(appRelativeUrl, {cache: "no-store"})
    const absoluteUrl = URLUtils.getAbsoluteUrl(appRelativeUrl);
    const reqInit = {
      method: "get",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Expires": "-1",
        "Pragma": "no-cache"
      }
    };
    return window.fetch(absoluteUrl, reqInit);
  }

  /**
   * This is ONLY used for testing as the logout invocation happens
   * via an ajax rpc (which doesn't handle (sso required) redirects appropriately).  During testing, can't force
   * an application browser reload as then lose test context (esp. for xspec tests where top-level page
   * is the karma default.html page).  The redirect to debug.html also does not have the 'required'
   * x-abinitio-response header as it is served by the karma server
   */
  async logoutForTesting() {
    // pre-emptively logout of direct proxies as part of logout as password is AG token tied to old jsessionid
    ProxyRPCService.reset();

    // ignore returned redirected content response (typically karma 'debug.html' ...)
    await this.rawJsonFetch("/authenticate/j_spring_security_logout?useReferer=true");
    /*
          // Below works, but always outputs a console.error about the 'failed to fetch' issue
          //    which causes console noise that is worrisome to debugging developers.
        try
        {
          // Do not follow redirect as tries to reload referer (karma debug.html) which is noise...
          // await this.rpcService.invokeVoid("/authenticate/j_spring_security_logout?useReferer=true",
          //     null, {"redirect": "error"})
        }
        catch (err)
        {
          // As redirect=error, Fetch will throw an error when the redirect is returned from the appserver.
          if (!FetchUtils.isFailedToFetchError(err))
          {
            console.error("Unexpected error during testLogout: " + err);
            throw err;
          }
        }
        return UserInfo.createAnonymousUser();
    */
  }
  changePassword(oldPassword, newPassword) {
    return this.rpcService.invokeRetObj("/api/abweb/user/actions/changePassword", {
      oldPassword,
      newPassword
    }, ChangePasswordResult, MethodType.POST);
  }
  getLoginHistory() {
    return this.rpcService.invokeRetObj("/api/abweb/user/loginHistory", null, LoginHistoryInfo, MethodType.GET);
  }

  /**
   * @inheritDoc
   */
  handleSSORedirect() {
    // See CAT-1503 for SSO workflow, including description of redirects at every step in sso chain.
    // After sso authentication succeeds, it will redirect back to this page (but user will be authenticated!)

    /*
     * For most apps, the SSO workflow happens as part of requesting its index.html, and this method is not typically used.
     * For the portal, its index.html is served by a front end webserver, so this handling is required.
     * 1. Browser goes to /portal/app/index.html
     * 2. Client detects no user, and no idpState param so redirects to /api/cass/securePreAuth (below)
     * 4. /api/cass/securePreAuth redirects to IDP, (telling it to redirect to /portal after login)
     * 5. After login, IDP redirects to /portal?idpState=....
     * 6. This gets redirected to /portal/app/index.html?idpState=...
     * 7. App loads again, still detects no user, but finds idpState, and redirects to /sso/index.html?idpState= (below)
     * 8. This time, the server intercepts this and sees the idpState stuff, and verifies idpState with IDP.
     * 9. Server then proceeds to evaluate /sso/index.html, but returns session cookie in response.
     * 10. /portal/sso/index.html redirects to /portal/app/index.html
     * 11. This time when the app loads, user is authenticated.
     */
    let search = document.location.search;
    if (search.startsWith("?")) {
      search = search.substring(1);
    }
    const params = URLUtils.queryStringToObject(search);
    if (params["state"] && (params["code"] || params["error"])) {
      BrowserUtils.setLocationHref(URLUtils.getAbsoluteUrl("/authenticate/sso.html") + document.location.search + document.location.hash);
    } else {
      const currentRelUrl = URLUtils.currentRootAppRelativeUrl; // everything to right of context root, starting w/'/'
      const secureAccessURL = URLUtils.addParamsToUrl("/api/cass/securePreAuth", {
        "redirect": currentRelUrl
      });
      BrowserUtils.setLocationHref(URLUtils.getAbsoluteUrl(secureAccessURL));
    }

    // As above is changing browser url, the existing browser page is going away.
    // Short-circuit rest of this call stack by throwing an error
    PromiseUtils.throwAlreadyHandled(AuthenticationService.SSO_REDIRECT_MSG);
  }

  /**  @inheritDoc */
  reenableConcurrentSession() {
    // Currently enabling a discabled concurrent session is done by getting a new csrf token
    return AbWebServices.csrfService().getToken();
  }

  /**  @inheritDoc */
  async determineLostSessionError(error) {
    const that = this;
    function buildLostSessionErrorContext(lostSessionErrorType = LostSessionErrorType.SESSION_INVALID) {
      const sso = AbWebServices.sessionData.currentUser.usesSSO || ClassUtils.isInstanceOf(error, RemoteError) && error.isSsoNoSessionError;
      return {
        error,
        sso,
        lostSessionErrorType
      };
    }
    let lostAuthentication;

    /**
     * They've lost authentication if current client user is authenticated (!anonymous) and the server user is not.
     * Used to reduce 'false positive' lost session workflows like CAT-1777
     */
    async function hasLostAuthentication() {
      if (lostAuthentication === undefined) {
        const currentClientUser = AbWebServices.sessionData.currentUser;
        if (currentClientUser.isAnonymous) {
          lostAuthentication = false; // Can't lose authentication if you never had it...
          LOG.debug("Calculated determineLostSession::hasLostAuthentication=false as anonymous user!!!!");
          return lostAuthentication;
        } else {
          const currentServerUser = await that.getAuthenticatedUserProbe(); // returns anonymous user if rpc throws
          lostAuthentication = currentServerUser == null || currentServerUser.isAnonymous // server user must be authenticated
          || currentServerUser.username !== currentClientUser.username; // same user
          LOG.debug(`Calculated determineLostSession::hasLostAuthentication = ${lostAuthentication} from getAuthenticatedUserProbe`);
          return lostAuthentication;
        }
      }
      LOG.debug(`Cached determineLostSession::hasLostAuthentication = ${lostAuthentication}`);
      return lostAuthentication;
    }
    const response = error.response;
    // Should log what triggers the authentication error to enable pfs diagnostics
    if (response && response.status === 401) {
      LOG.error("Received 401 which is treated as an authentication error.");
      return buildLostSessionErrorContext();
    }
    // If have non-abinitio response, assume it is a (siteminder?) redirect and need to re-authenticate
    else if (ClassUtils.isInstanceOf(error, NonAbInitioResponseError)
    // CAT-1777: proxy returned 500 when upstream request to app server timed out.  This did NOT cause the user
    // session to be lost, so verify via subsequent 'userInfo' call as to whether user has lost session
    && (await hasLostAuthentication())) {
      LOG.error("Received non-abinitio response which is treated as an authentication error.");
      return buildLostSessionErrorContext();
    }
    // If got unexpected content or content-type, assume it is because of an unexpected redirect to html login page
    // Example: expected response to be 'application/json', but instead got a 'text/html' response back
    else if (ClassUtils.isInstanceOf(error, UnexpectedResponseContentError)
    // Iff user has lost session, trigger lost session error
    && (await hasLostAuthentication())) {
      LOG.error("Received UnexpectedResponseContentError which is treated as an authentication error.  Caused by: " + error.message);
      return buildLostSessionErrorContext();
    }
    if (ClassUtils.isInstanceOf(error, RemoteError)) {
      const remoteError = error;
      if (remoteError.isAuthenticationError || remoteError.isNoSessionError) {
        LOG.error("Received server RemoteError that was flagged as an authentication error or a no session error.");
        return buildLostSessionErrorContext();
      }
      if (remoteError.isConcurrentSessionInvalid) {
        LOG.error("Received server RemoteError that was flagged as a Concurrent Session Invalid.");
        return buildLostSessionErrorContext(LostSessionErrorType.CONCURRENT_SESSION_INVALID);
      }
      if (remoteError.isConcurrentSessionDisabled) {
        LOG.error("Received server RemoteError that was flagged as a Concurrent Session Disabled.");
        return buildLostSessionErrorContext(LostSessionErrorType.CONCURRENT_SESSION_DISABLED);
      }
      if (remoteError.isStolenSessionError) {
        LOG.error("Received server RemoteError that was flagged as a Stolen Session.");
        return buildLostSessionErrorContext(LostSessionErrorType.BROWSER_TAB_STOLEN_SESSION);
      }

      // Remote error that is not session related, return null
      LOG.debug("determineLostSession returning null for RemoteError error: " + ErrorUtils.getErrorMsgFromObj(error));
      return null;
    }

    // Not sure the below 'browser json parsing' error logic is still needed
    // as the 'UnexpectedResponseContentError' case above probably now triggers before getting here.
    // But just in case...

    // Unfortunately, due to some conflicts with Spring security, the server may not get far enough to return an
    // authorization error. Instead, the server will redirect the user to the old login.jsp (which in turn may redirect
    // to the application root!). We never really want that to happen in this world of API calls. What ends up happening
    // is the server returns an HTML page as an OK (200) response. The fetch layer assumes all is well, and tries to
    // parse the html response as json. The result is SOME form of Error being triggered. Each browser triggers a different
    // type of error, so this is trying to accommodate that. Note that these messages may change between browser version,
    // so this could prove to be rather fragile.
    const isJsonParseError = this.isJsonParseErrorFromFirefox(error) || this.isJsonParseErrorFromEdge(error) || this.isJsonParseErrorFromChrome(error);
    if (isJsonParseError && (await hasLostAuthentication())) {
      LOG.error("Error while parsing json payload which is treated as an authentication error");
      return buildLostSessionErrorContext();
    }

    // Not a lost session error
    LOG.debug("determineLostSession returning null for error: " + ErrorUtils.getErrorMsgFromObj(error));
    return null;
  }
  isJsonParseErrorFromFirefox(error) {
    return ClassUtils.isInstanceOf(error, SyntaxError) && error.message.indexOf("JSON.parse: unexpected character") > -1;
  }
  isJsonParseErrorFromEdge(error) {
    return ClassUtils.isInstanceOf(error, DOMException) && error.message === "Failed to read.";
  }
  isJsonParseErrorFromChrome(error) {
    return ClassUtils.isInstanceOf(error, SyntaxError) && error.message.indexOf("Unexpected token < in JSON at position") > -1;
  }
}
_defineProperty(AuthenticationService, "SSO_REDIRECT_MSG", "SSO Redirection in progress");
_defineProperty(AuthenticationService, "USER_INFO_RPC_PATH", "/api/abweb/user/userInfo");
//# sourceMappingURL=AuthenticationService.js.map
